import { useState, type FormEvent } from 'react';
import { useOp } from '../api.ts';
import { Link, useRouter } from '../router.tsx';
import type { MerchantList } from '../types.ts';
import { runWrite } from '../ui/changes.ts';
import { ErrorBox } from '../ui/kit.tsx';
import { useToast } from '../ui/toasts.tsx';
import { CouponFactsFields, emptyFacts, factsToInput } from './coupon-facts.tsx';

export function CouponCreate() {
  const { query, navigate } = useRouter();
  const toast = useToast();
  const merchants = useOp<MerchantList>('admin.merchants.list', { limit: 200 });
  const [merchantRef, setMerchantRef] = useState(query.get('merchant') ?? '');
  const [draft, setDraft] = useState(emptyFacts);
  const [offerTo, setOfferTo] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const sites = merchants.data?.sites ?? [];
  const selectedSites = offerTo ?? sites.map((s) => s.ref);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const result = await runWrite<{ ref: string; offeredTo: string[] }>(toast, 'admin.coupons.create', { merchantRef, ...factsToInput(draft), offerTo: selectedSites }, (r) =>
      `Created; waiting for review on ${r.offeredTo.length} site${r.offeredTo.length === 1 ? '' : 's'}`,
    );
    setSaving(false);
    if (result) navigate(`/coupons?coupon=${result.ref}`);
  };

  return (
    <section className="page narrow">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/coupons">Coupons</Link> <span aria-hidden="true">/</span> <span>New</span>
      </nav>
      <header className="page-head">
        <h1>New coupon</h1>
      </header>
      {merchants.error ? <ErrorBox error={merchants.error} onRetry={merchants.reload} /> : null}
      <form className="card" onSubmit={submit}>
        <label>
          Merchant
          <select required value={merchantRef} onChange={(e) => setMerchantRef(e.target.value)}>
            <option value="">Choose a merchant…</option>
            {merchants.data?.rows.map((m) => (
              <option key={m.ref} value={m.ref}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <CouponFactsFields draft={draft} onChange={setDraft} />
        <fieldset className="radio-row">
          <legend>Offer to sites (each starts in review)</legend>
          {sites.map((site) => (
            <label key={site.ref} className="inline-check">
              <input
                type="checkbox"
                checked={selectedSites.includes(site.ref)}
                onChange={(e) => setOfferTo(e.target.checked ? [...selectedSites, site.ref] : selectedSites.filter((r) => r !== site.ref))}
              />
              {site.name}
            </label>
          ))}
        </fieldset>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || !merchantRef}>
            {saving ? 'Creating…' : 'Create coupon'}
          </button>
          <Link to="/coupons" className="btn btn-ghost">
            Cancel
          </Link>
        </div>
      </form>
    </section>
  );
}
